/* eslint-disable @angular-eslint/no-output-native */

import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject, input, output } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { MatProgressBar } from '@angular/material/progress-bar';
import { EMPTY, catchError, of, switchMap, take, tap } from 'rxjs';
import { IMedia } from 'src/app/api/modules/core/dynamic/components/IMedia';
import { FaIconComponent } from '../../../../icons/components/fa-icon/fa-icon.component';
import { DomIntersectionObserverDirective } from '../../../directives/dom-intersection-observer.directive';
import { MediaNativeAudioComponent } from './media-native-audio/media-native-audio.component';
import { MediaNativeVideoComponent } from './media-native-video/media-native-video.component';
import { MediaUIComponent } from './media-ui/media-ui.component';
import { MediaVimeoComponent } from './media-vimeo/media-vimeo.component';
import { MediaYouTubeComponent } from './media-youtube/media-youtube.component';
import { MediaRepository } from './media.repository';
import { MediaService } from './media.service';
import { MediaTypes } from './media.types';

/**
 * The Media Component is used to wrap multiple types of media.
 */
@Component({
  selector: 'app-media',
  templateUrl: './media.component.html',
  styleUrls: ['./media.component.scss'],
  providers: [MediaService, MediaRepository],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatProgressBar,
    FaIconComponent,
    DomIntersectionObserverDirective,
    MediaYouTubeComponent,
    MediaVimeoComponent,
    MediaNativeVideoComponent,
    MediaNativeAudioComponent,
    MediaUIComponent,
  ],
})
export class MediaComponent implements OnInit {
  private readonly mediaService = inject(MediaService);
  protected readonly mediaRepository = inject(MediaRepository);
  private readonly destroyRef = inject(DestroyRef);

  // observable bridges over the repository signals, created eagerly here (in the
  // injection context) so the lifecycle callbacks below can subscribe to them
  private readonly isMediaPortrait$ = toObservable(this.mediaRepository.isMediaPortrait);
  private readonly hasError$ = toObservable(this.mediaRepository.error);
  private readonly isPlaying$ = toObservable(this.mediaRepository.isPlaying);
  private readonly isPaused$ = toObservable(this.mediaRepository.isPaused);
  private readonly currentTime$ = toObservable(this.mediaRepository.currentTime);
  private readonly duration$ = toObservable(this.mediaRepository.duration);
  private readonly progress$ = toObservable(this.mediaRepository.progress);
  private readonly ended$ = toObservable(this.mediaRepository.ended);
  private readonly pauseAsNotActiveMedia$ = toObservable(this.mediaRepository.pauseAsNotActiveMedia);
  private readonly shouldPauseWhenNoLongerIntersecting$ = toObservable(this.mediaRepository.shouldPauseWhenNoLongerIntersecting);

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  // CONFIG
  ////////////////////////////////////////////////////////////////////////////////////////////////////

  /**
   * The Configuration.
   *
   * It is applied once when the component initializes; the media cannot be
   * reconfigured afterwards (the players read their settings a single time).
   */
  readonly config = input.required<IMedia>();

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  // OUTPUTS
  ////////////////////////////////////////////////////////////////////////////////////////////////////

  /**
   * Is the media player ready?
   */
  readonly ready = output<void>();

  /**
   * Is the media player playing?
   */
  readonly playing = output<void>();

  /**
   * Is the media player paused?
   */
  readonly paused = output<void>();

  /**
   * Is the media player ended?
   */
  readonly ended = output<void>();

  /**
   * The Media player current time in seconds.
   */
  readonly currentTime = output<number>();

  /**
   *  The Media player duration in seconds.
   */
  readonly duration = output<number>();

  /**
   * The Media player progress in percentage from 0 - 100.
   */
  readonly progress = output<number>();

  /**
   * Occurs when the Media has experienced an error.
   */
  readonly error = output<void>();

  /**
   * Is the Media Source portrait?
   */
  readonly isPortrait = output<boolean>();

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  // LIFECYCLE
  ////////////////////////////////////////////////////////////////////////////////////////////////////

  /**
   * Lifecycle Hook
   */
  ngOnInit() {
    // configure the system; the store replays state so the subscriptions
    // created below always observe the configured values
    this.mediaService.applyConfiguration(this.config());

    // initialize
    this.initialize();
  }

  /**
   * Initialize the Media Player.
   */
  private initialize() {
    // listen out for if the media is portrait, this is required
    // so that users of the Media Object can determine if special
    // layout should be taken.
    this.isMediaPortrait$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((isPortrait) => {
      this.isPortrait.emit(isPortrait);
    });

    // listen out for if the media throws an error
    this.hasError$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((error) => {
      if (error === true) {
        this.error.emit();
      }
    });

    // listen out for when the application is configured via JSON so we know the type
    this.mediaRepository.configuredType$
      .pipe(
        // we only need to do this once
        take(1),
        // return the correct initialize sequence based on the type
        switchMap((type) => {
          switch (type) {
            case MediaTypes.youtube:
              return this.initializeYouTube();
            case MediaTypes.vimeo:
              return this.initializeVimeo();
            case MediaTypes.video:
              return this.initializeNativeVideo();
            case MediaTypes.audio:
              return this.initializeNativeAudio();
          }
        }),
        catchError((error: Error) => {
          // inform the store of the error
          this.mediaService.applyError(error?.message);

          // and return an empty observable
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        // we can consider the system initialized at this point
        this.initialized();
      });
  }

  /**
   * Initialize the YouTube Sequence.
   *
   *    1. Load the player (only once globally)
   *    2. Get the configuration
   *    3. Get the video details from the OEmbed API
   */
  private initializeYouTube() {
    return this.mediaService.applyYouTubePlayerLoad().pipe(
      take(1),
      switchMap(() => {
        // the configured-type gate guarantees the settings exist by now
        const configuration = this.mediaRepository.youtubeSettings();
        if (!configuration) {
          throw new Error('No YouTube settings available.');
        }
        return this.mediaService.retrieveYouTubeVideoDetails(configuration.videoId);
      }),
      catchError((error: HttpErrorResponse) => {
        this.mediaService.applyError(error?.message);
        return EMPTY;
      }),
      tap((details) => {
        this.mediaService.applyYouTubeVideoDetails(details);
      }),
    );
  }

  /**
   * Initialize the Vimeo Sequence.
   *
   *    1. Load the player (only once globally)
   *    2. Get the configuration
   *    3. Get the video details from the OEmbed API
   */
  private initializeVimeo() {
    return this.mediaService.applyVimeoPlayerLoad().pipe(
      take(1),
      switchMap(() => {
        // the configured-type gate guarantees the settings exist by now
        const configuration = this.mediaRepository.vimeoSettings();
        if (!configuration) {
          throw new Error('No Vimeo settings available.');
        }
        return this.mediaService.applyRetrieveVimeoVideoDetails(configuration.id, configuration.url);
      }),
      catchError((error: HttpErrorResponse) => {
        this.mediaService.applyError(error?.message);
        return EMPTY;
      }),
      tap((details) => {
        this.mediaService.applyVimeoVideoDetails(details);
      }),
    );
  }

  /**
   * Initialize the Video Sequence.
   *
   * This is easy because all details have already been provided in the configuration
   */
  private initializeNativeVideo() {
    // all details were provided upfront in the configuration
    this.mediaService.applyNativeVideoDetails();
    return of(true);
  }

  /**
   * Initialize the Audio Sequence.
   *
   * This is easy because all details have already been provided in the configuration
   */
  private initializeNativeAudio() {
    // all details were provided upfront in the configuration
    this.mediaService.applyNativeAudioDetails();
    return of(true);
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  // INITIALIZED
  ////////////////////////////////////////////////////////////////////////////////////////////////////

  /**
   * When the system has been configured, and loaded the required providers and video information (where applicable)
   * then the system is considered initialized and is ready to create the player.
   */
  private initialized() {
    // listen out for when the media provider is ready and emit the ready event
    this.mediaRepository.ready$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.ready.emit();
    });

    // listen out for when the media is playing and emit the playing event
    this.isPlaying$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((playing) => {
      if (playing) {
        // if the media is playing, inform the global media manager of this ID.
        this.mediaService.applyActiveMedia(this.mediaRepository.mediaID());
        this.playing.emit();
      }
    });

    // listen out for when the media is paused and emit the paused event
    this.isPaused$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((paused) => {
      if (paused) {
        this.paused.emit();
      }
    });

    // listen out for the currentTime and emit the currentTime event
    this.currentTime$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((currentTime) => {
      this.currentTime.emit(currentTime);
    });

    // listen out for the duration and emit the duration event
    this.duration$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((duration) => {
      this.duration.emit(duration);
    });

    // listen out for the progress and emit the progress event
    // this is a percentage based on the current time and duration
    this.progress$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((progress) => {
      this.progress.emit(progress);
    });

    // listen out for when the media is ended and emit the ended event
    this.ended$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((ended) => {
      if (ended) {
        this.ended.emit();
      }
    });

    // listen out for when the global playing media is not this media
    this.pauseAsNotActiveMedia$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((pause) => {
      if (pause) {
        this.pauseMedia();
      }
    });

    // pause when not intersecting
    this.shouldPauseWhenNoLongerIntersecting$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((shouldPause) => {
      if (shouldPause) {
        this.pauseMedia();
      }
    });

    // finally this component has initialized
    this.mediaService.applyInitialized();
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  // API CONTROLS
  ////////////////////////////////////////////////////////////////////////////////////////////////////

  /**
   * Play the Media.
   */
  playMedia() {
    this.mediaService.play();
  }

  /**
   * Pause the Media.
   */
  pauseMedia() {
    this.mediaService.pause();
  }

  /**
   * Seek the Media at a specific time.
   *
   * @param seconds - The time in seconds to seek to.
   * @param allowSeekAhead - Whether the player is allowed to make a new request to the server if it determines that the
   * current playback position is too far ahead of the next buffered position. This value defaults to true.
   * @param play - Whether the player should play the video after seeking to the given time.
   */
  seekMediaTo(seconds: number, allowSeekAhead = true, play = false) {
    this.mediaService.seekTo(seconds, allowSeekAhead, play);
  }

  /**
   * Mute the media.
   */
  muteMedia() {
    this.mediaService.mute();
  }

  /**
   * Unmute the media.
   */
  unMuteMedia() {
    this.mediaService.unMute();
  }

  /**
   *  Set the media volume.
   *
   * @param volume - a number between 0 and 100
   */
  setMediaVolume(volume: number) {
    if (typeof volume === 'number' && volume >= 0 && volume <= 100) {
      this.mediaService.volume(volume);
    }
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  // TEMPLATE METHODS
  ////////////////////////////////////////////////////////////////////////////////////////////////////

  /**
   * Is this media intersecting with the viewport?
   *
   * @param intersecting - true if so, false if not.
   */
  isIntersecting(intersecting: boolean) {
    // inform the store of the intersection state
    this.mediaService.applyIsIntersecting(intersecting);
  }
}
